import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import classNames from 'classnames'

import { MoreIcon, CreateNoteIcon } from 'components/Utils/Icons'
import IconButton from 'components/Utils/IconButton'
import PopoverOnClick from 'components/Utils/Popover/PopoverOnClick'
import NoteControlsMenu from 'components/Notes/Controls/Menu'
import { TooltipOnHover } from 'components/Utils/TooltipOnHover'

import { selectors as NotesSelectors } from 'data/notes'
import { selectors as ChannelsSelectors } from 'data/channels'
import { trackNewNoteClicked } from 'data/analytics/actions'

import { currentUserCanCreateNote } from 'data/rights/selectors'

import { provideCreateNote } from '../../dataSync/provideCreateNote'

const preventDefault = (event: React.MouseEvent) => {
  // prevent the click on the dropdown button to trigger the note row Link behaviour
  event.preventDefault()
  event.stopPropagation()
}

export type NoteControlsProps = {
  className?: string
  noteId: string
  depth: number
  onMorePopoverIsOpenChange: (isOpen: boolean) => void
  createNote: (
    value: { channelId: string; parentNoteId: string }
  ) => Promise<{ result: any; error?: Error }>
  onNoteCreated: (note: any) => void
}

const NoteControls = ({
  className,
  noteId,
  onMorePopoverIsOpenChange,
  createNote,
  onNoteCreated,
  depth,
}: NoteControlsProps) => {
  const dispatch = useDispatch()
  const note = useSelector(state => NotesSelectors.getNote(state, noteId))
  const canCreate = useSelector(state => {
    if (note.archivedAt) {
      return false
    }
    const channel = note && ChannelsSelectors.getChannel(state, note.channelId)
    return currentUserCanCreateNote(state, channel)
  })

  const onCreateNote = React.useCallback(
    async event => {
      event.preventDefault()
      event.stopPropagation()

      dispatch(trackNewNoteClicked({ source: 'plus_button', depth }))

      const { result, error } = await createNote({
        channelId: note.channelId,
        parentNoteId: note.id,
      })
      if (result && !error) {
        onNoteCreated(result.data.createNote)
      }
    },
    [onNoteCreated, createNote, note]
  )

  const handlePopoverIsOpenChange = ({ isOpen }: { isOpen: boolean }) =>
    onMorePopoverIsOpenChange(isOpen)

  return (
    <div
      className={classNames('flexbox flexbox-centered shade-30', className)}
      onClick={preventDefault}
    >
      <PopoverOnClick
        position="right"
        align="start"
        onIsOpenChange={handlePopoverIsOpenChange}
        content={({ closePopover }: { closePopover: () => void }) => (
          <NoteControlsMenu
            noteId={noteId}
            showListControls
            placement="right"
            onRowClick={closePopover}
          />
        )}
      >
        <div className="flexbox flexbox-align-center">
          <IconButton colorScheme="grayOnGray" boxSize="smaller">
            <MoreIcon size={14} />
          </IconButton>
        </div>
      </PopoverOnClick>
      {canCreate && (
        <IconButton
          colorScheme="whiteOnPrimary"
          boxSize="smaller"
          style={{
            marginLeft: 1,
            marginRight: 1,
          }}
          className={classNames('margin-horizontal-small')}
          onClick={onCreateNote}
        >
          <TooltipOnHover
            id="add-note-below"
            placement="top"
            tooltipContent="Add doc below"
          >
            <CreateNoteIcon size={12} />
          </TooltipOnHover>
        </IconButton>
      )}
    </div>
  )
}

export const EnhancedNoteControls = provideCreateNote(NoteControls)
